import { useState } from "react";
import { Check, X, Minus } from "lucide-react";

/* Competitor pricing reflects published consumer-tier pricing as of 2026. */
const cols = [
  { name: "SmartPlaite", price: "Free – $9.99/mo", featured: true },
  { name: "Cal AI", price: "~$5.99/mo", featured: false },
  { name: "MyFitnessPal", price: "~$19.99/mo", featured: false },
  { name: "Lose It!", price: "~$3.33/mo", featured: false },
  { name: "Cronometer", price: "~$8.99/mo", featured: false },
  { name: "Lifesum", price: "~$4.17/mo", featured: false },
  { name: "Noom", price: "~$70/mo", featured: false },
  { name: "Whoop", price: "$30/mo", featured: false },
];

/* Each row: capability label + 8 cells aligned to cols above. */
const rows = [
  ["Live MIQ score (holistic 0–100)", true, false, false, false, false, false, false, false],
  ["One AI brain — meals + sleep + training + supps + biometrics", true, false, false, false, false, false, "partial", "partial"],
  ["Telehealth — on-demand licensed clinicians", true, false, false, false, false, false, "partial", false],
  ["AI photo plate scan", true, true, "partial", "partial", false, "partial", false, false],
  ["Barcode + manual + restaurant-aware logging", true, "partial", true, true, true, true, "partial", false],
  ["Restaurant-aware AI (\"what should I eat at Chipotle?\")", true, false, false, false, false, false, false, false],
  ["AI Recipe Generator from your kitchen", true, false, false, false, false, "partial", false, false],
  ["SmartMeals — % Perfect Fit matching", true, false, false, false, false, false, false, false],
  ["Smart Swaps for goal optimization", true, false, false, "partial", "partial", "partial", "partial", false],
  ["10+ micronutrient tracking", true, false, false, false, true, false, false, false],
  ["Daily wellness check-ins (sleep / stress / energy)", true, false, false, false, false, "partial", "partial", true],
  ["Personalized AI workout & rehab planner", true, false, false, false, false, "partial", "partial", false],
  ["Peptide + supplement intelligence", true, false, false, false, "partial", false, false, false],
  ["🩸 Bloodwork & Biomarker OCR (lab PDFs → MIQ)", true, false, false, false, "partial", false, false, false],
  ["🎙️ Voice meal logging in 50+ languages", true, false, "partial", false, false, false, false, false],
  ["Wearable integrations (Apple, Google, Whoop, Oura, Garmin, Fitbit, Samsung)", true, "partial", "partial", "partial", true, "partial", false, true],
  ["Weekly Intelligence Brief", true, false, false, false, false, false, false, "partial"],
];

const renderCell = (v, featured) => {
  if (v === true)
    return <Check className={`w-5 h-5 mx-auto ${featured ? "text-brand-free" : "text-white/65"}`} />;
  if (v === false) return <X className="w-4 h-4 text-white/20 mx-auto" />;
  if (v === "partial")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-white/45 uppercase tracking-[0.12em]">
        <Minus className="w-3 h-3" /> partial
      </span>
    );
  return <span className="font-mono text-xs text-white/85 whitespace-nowrap">{v}</span>;
};

export default function Comparison() {
  // Competitor picker for the mobile compact view; default = Cal AI (most direct rival)
  const competitors = cols.slice(1); // everyone except SmartPlaite
  const [pickedIdx, setPickedIdx] = useState(0);
  const picked = competitors[pickedIdx];

  return (
    <section id="compare" data-testid="comparison-section" className="relative py-28 md:py-36 bg-[#070707] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brand-accent font-bold mb-5">Head to head</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            One brain vs. <span className="text-gradient-hero">every other tracker</span>.
          </h2>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
            AI calorie trackers count macros. Coaches send recipes. Wearables monitor recovery.
            SmartPlaite does all three — plus telehealth and restaurant-aware AI — for less than a coffee.
          </p>
        </div>

        {/* DESKTOP: full 8-col table */}
        <div className="hidden lg:block mt-14 glass-strong rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm" data-testid="comparison-table-desktop">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-6 py-5 text-[11px] uppercase tracking-[0.18em] text-white/45 font-bold font-sans">
                    Capability
                  </th>
                  {cols.map((c) => (
                    <th
                      key={c.name}
                      data-testid={`comparison-col-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-2.5 py-5 text-center font-sans ${c.featured ? "bg-brand-accent/[0.06]" : ""}`}
                    >
                      <div className={`font-display font-bold text-sm tracking-tight ${c.featured ? "text-brand-accent" : "text-white/85"}`}>
                        {c.name}
                      </div>
                      <div className={`mt-1 font-mono text-[10px] ${c.featured ? "text-brand-free" : "text-white/45"}`}>
                        {c.price}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, ...cells], idx) => (
                  <tr key={label} data-testid={`comparison-row-${idx + 1}`} className="border-b border-white/5 last:border-0">
                    <td className="px-6 py-4 text-sm text-white/85 leading-snug">{label}</td>
                    {cells.map((c, j) => (
                      <td
                        key={j}
                        className={`px-2.5 py-4 text-center ${cols[j].featured ? "bg-brand-accent/[0.06]" : ""}`}
                      >
                        {renderCell(c, cols[j].featured)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE: SmartPlaite vs picked competitor */}
        <div className="lg:hidden mt-12" data-testid="comparison-mobile">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">
            Compare SmartPlaite vs
          </div>
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-6 px-6 fade-edges" data-testid="comparison-picker">
            {competitors.map((c, i) => (
              <button
                key={c.name}
                onClick={() => setPickedIdx(i)}
                data-testid={`comparison-pick-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`shrink-0 pill px-4 py-2 text-xs font-semibold border transition-colors ${
                  i === pickedIdx
                    ? "bg-brand-accent text-white border-brand-accent"
                    : "bg-white/[0.03] text-white/65 border-white/10 hover:bg-white/10"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="mt-5 glass-strong rounded-3xl overflow-hidden">
            <div className="grid grid-cols-[1.4fr_auto_auto] gap-x-4 px-5 py-5 border-b border-white/5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold self-end">
                Capability
              </div>
              <div className="text-center min-w-[68px]">
                <div className="font-display font-bold text-xs text-brand-accent">SmartPlaite</div>
                <div className="font-mono text-[9px] text-brand-free mt-0.5">Free – $9.99/mo</div>
              </div>
              <div className="text-center min-w-[68px]">
                <div className="font-display font-bold text-xs text-white/85">{picked.name}</div>
                <div className="font-mono text-[9px] text-white/45 mt-0.5">{picked.price}</div>
              </div>
            </div>
            {rows.map(([label, ...cells], idx) => {
              // cells[0] = SmartPlaite, cells[pickedIdx+1] = picked
              const spv = cells[0];
              const opv = cells[pickedIdx + 1];
              return (
                <div
                  key={label}
                  data-testid={`comparison-mobile-row-${idx + 1}`}
                  className="grid grid-cols-[1.4fr_auto_auto] gap-x-4 items-center px-5 py-4 border-b border-white/5 last:border-0"
                >
                  <div className="text-[13px] text-white/85 leading-snug">{label}</div>
                  <div className="text-center min-w-[68px] bg-brand-accent/[0.06] py-3 rounded-lg">
                    {renderCell(spv, true)}
                  </div>
                  <div className="text-center min-w-[68px] py-3">{renderCell(opv, false)}</div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[10px] text-white/35 text-center">
            Tap a competitor pill above to switch the comparison.
          </p>
        </div>

        <p className="mt-6 text-[11px] text-white/35 leading-relaxed max-w-4xl">
          Competitor pricing reflects publicly listed consumer plans as of 2026 and may vary by region, plan length, or promotion. SmartPlaite tiers: Free $0, Smart $4.99/mo ($41.99/yr), Genius $9.99/mo ($83.99/yr) — both annuals save 30%. MIQ score, bloodwork OCR, and wearable sync are Genius-tier features. Telehealth is available on every tier. Cronometer accepts manual biomarker entry but does not OCR lab PDFs or connect them to a holistic intelligence score. A 1-on-1 personal trainer typically runs $300+/month and is in a different category entirely.
        </p>
      </div>
    </section>
  );
}
